import { subscribeToAgentForceStartEvent } from './customerServiceIntegration/subscribeToAgentForceStartEvent'
import { subscribeToMIAWStartEvent } from './customerServiceIntegration/subscribeToMIAWStartEvent'
import getConfig from './miawConfig'
import { EmbeddedServiceBootstrap, BotType, MiawConfig } from './types'
import { setBotType } from './utils'
import loadScript from './utils/loadScript'
import { logDevelopmentMessage } from './utils/logDevelopmentMessage'
import { getPageStatus } from './customerServiceIntegration/getPageStatus'
import { FEATURE_SHOW_MIAW_CHAT } from '../../consts/env'
import { pollForWidgetFlagAndShowAppropriateWidget } from './utils/pollForWidgetFlag'
import { dispatchOmnitureEvent } from './utils/general'

declare var embeddedservice_bootstrap: EmbeddedServiceBootstrap

type UserData = {
  email?: string
  shopperId?: string
  name?: string
}

export const shouldShowMIAWChat = () => {
  const showMIAWChatButton = FEATURE_SHOW_MIAW_CHAT && !getPageStatus().isInCustomerServicePage
  logDevelopmentMessage(`showMIAWChatButton: ${showMIAWChatButton}`)
  return showMIAWChatButton
}

export const showMIAWChat = () => {
  if (shouldShowMIAWChat() && !embeddedservice_bootstrap?.utilAPI?.getEmbeddedMessagingFrame()) {
    toggleMIAWChatVisibility(true)
  }
}

export const onTimeElapsedHandler = () => showMIAWChat()

async function initializeMIAW(_config: MiawConfig, botType: BotType) {
  const userData = await fetchUserData()
  logDevelopmentMessage(`Initialising:: ${_config.eswConfigDevName}`)
  try {
    window.addEventListener('onEmbeddedMessagingReady', () => {
      configurePrechatFields(userData)
    })

    window.addEventListener('onEmbeddedMessagingConversationStarted', () => {
      setBotType(botType)
      dispatchOmnitureEvent('dataStateChange', {
        action: 'messaging',
        type: 'start',
      })
    })

    window.addEventListener('onEmbeddedMessagingConversationRouted', (event) => {
      const payload = JSON.parse(event.detail.conversationEntry.entryPayload)
      if (payload.routingType === 'Transfer')
        dispatchOmnitureEvent('dataStateChange', {
          action: 'messaging',
          type: 'established',
        })
    })

    if (embeddedservice_bootstrap) {
      embeddedservice_bootstrap.settings.language = 'en_US'
      embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true
      embeddedservice_bootstrap.init(_config.orgID, _config.eswConfigDevName, _config.siteURL, {
        scrt2URL: _config.snippetConfigURL,
      })

      const pollArgs = { onTimeElapsed: onTimeElapsedHandler, maxWaitTime: 15000 }
      pollForWidgetFlagAndShowAppropriateWidget(pollArgs)
    }
  } catch (err) {
    logDevelopmentMessage(`[MIAW] Error initializing MIAW chat:${err}`)
    throw new Error(`[MIAW] initialization error: ${(err as Error).message}`)
  }
}

async function fetchUserData(): Promise<UserData> {
  const userData = (await window.auth0?.getUser()) || {}
  return {
    ...userData,
    shopperId: userData['https://www.johnlewis.com/auth/customerId'] || '',
  }
}

function configurePrechatFields(userData: UserData) {
  if (embeddedservice_bootstrap) {
    embeddedservice_bootstrap.prechatAPI?.setHiddenPrechatFields({
      IDV_verification_tc: JSON.stringify({
        ...(userData.shopperId && { shopperId: userData.shopperId }),
      }),
      ChatStartPageURL: window.location.href.slice(0,255),
    })
    embeddedservice_bootstrap.prechatAPI?.setVisiblePrechatFields({
      _firstName: {
        value: userData?.name || '',
        isEditableByEndUser: true,
      },
      _email: {
        value: userData?.email || '',
        isEditableByEndUser: userData.shopperId === '',
      },
    })
  }
}

export const toggleMIAWChatVisibility = async (toShow: boolean) => {
  if (embeddedservice_bootstrap?.utilAPI) {
    if (toShow) {
      logDevelopmentMessage('showing MIAW chat button')
      embeddedservice_bootstrap.utilAPI.showChatButton()
    } else {
      logDevelopmentMessage('hiding MIAW chat button')
      embeddedservice_bootstrap.utilAPI.hideChatButton()
    }
  }
}

export async function loadMIAW(_type = BotType.DEFAULT) {
  const eventSubscriptions: { [key in BotType]?: () => void } = {
    [BotType.DEFAULT]: subscribeToMIAWStartEvent,
    [BotType.AGENT_FORCE]: subscribeToAgentForceStartEvent,
  }

  try {
    const config = getConfig(BotType.DEFAULT)
    await loadScript(`${config.siteURL}/assets/js/bootstrap.min.js`)
    await initializeMIAW(config, _type)
    eventSubscriptions[_type]?.()
  } catch (err) {
    console.error(`[${_type}] Failed to load MIAW:`, err)
  }
}
